/**
 * VerifyEmailPage.tsx
 * Public route: /verify-email?token=...
 *
 * Called when the user clicks the verification link in their inbox.
 * Calls GET /api/auth/verify-email?token=..., which marks the account
 * verified and creates a session. On success we invalidate /auth/me and
 * redirect to /home so the user lands logged-in.
 */

import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import WorkRoamerBrand from '@/components/WorkRoamerBrand';
import { getGetMeQueryKey } from '@workspace/api-client-react';

type VerifyState = 'loading' | 'success' | 'error' | 'already';

export default function VerifyEmailPage() {
  const search          = useSearch();
  const [, setLocation] = useLocation();
  const queryClient     = useQueryClient();

  const token = new URLSearchParams(search).get('token') ?? '';

  const [state,       setState]       = useState<VerifyState>('loading');
  const [errorMsg,    setErrorMsg]    = useState('');

  // Resend flow (shown on error)
  const [resendEmail,  setResendEmail]  = useState('');
  const [resending,    setResending]    = useState(false);
  const [resendStatus, setResendStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('No verification token found. Please use the link from your email.');
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        const res  = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
          credentials: 'include',
        });
        const data = await res.json() as {
          verified?: boolean;
          alreadyVerified?: boolean;
          message?: string;
          error?: string;
        };

        if (cancelled) return;

        if (res.ok && data.verified) {
          // Session cookie already set by the server. Invalidate /auth/me so
          // Shell picks up the authenticated session immediately on redirect.
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          try { sessionStorage.setItem('nomad:fresh-signup', '1'); } catch { /* ignore */ }
          setState('success');
          setTimeout(() => setLocation('/home'), 2000);
        } else if (res.ok && data.alreadyVerified) {
          setState('already');
        } else {
          setState('error');
          setErrorMsg(data.error ?? 'The verification link is invalid or has expired.');
        }
      } catch {
        if (!cancelled) {
          setState('error');
          setErrorMsg('Network error. Please check your connection and try again.');
        }
      }
    }

    verify();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResending(true);
    setResendStatus(null);
    try {
      const res  = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail.trim() }),
      });
      const data = await res.json() as { message?: string; devToken?: string };
      const devNote = data.devToken ? ` (dev token: ${data.devToken})` : '';
      setResendStatus(`✅ ${data.message ?? 'Sent.'}${devNote}`);
    } catch {
      setResendStatus('❌ Network error. Please try again.');
    } finally {
      setResending(false);
    }
  }

  // ── Shared page shell ─────────────────────────────────────────────────────
  function PageShell({ children }: { children: React.ReactNode }) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
        padding: '20px 16px',
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          boxSizing: 'border-box' as const,
          padding: 'clamp(28px,6vw,44px) clamp(20px,5.5vw,36px)',
          background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)',
          borderRadius: 20,
          border: '1.5px solid rgba(59,130,246,0.40)',
          boxShadow: [
            '0 0 40px rgba(37,99,235,0.25)',
            '0 0 80px rgba(37,99,235,0.10)',
            'inset 0 1px 0 rgba(255,255,255,0.04)',
          ].join(', '),
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
        }}>
          <WorkRoamerBrand width={260} style={{ marginBottom: 28 }} />
          {children}
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <PageShell>
        <Loader2 size={44} color="#3b82f6"
          style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', textAlign: 'center' }}>
          Verifying your email…
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 8, textAlign: 'center' }}>
          This will only take a moment.
        </div>
      </PageShell>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (state === 'success') {
    return (
      <PageShell>
        <CheckCircle2 size={52} color="#22c55e" strokeWidth={1.5}
          style={{ filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.5))', marginBottom: 16 }} />
        <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', textAlign: 'center', letterSpacing: '0.01em' }}>
          Email Verified!
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 10, textAlign: 'center', lineHeight: 1.6 }}>
          Your account is confirmed. Taking you to Mission Control…
        </div>
        <Loader2 size={18} color="#3b82f6"
          style={{ animation: 'spin 1s linear infinite', marginTop: 18 }} />
      </PageShell>
    );
  }

  // ── Already verified ──────────────────────────────────────────────────────
  if (state === 'already') {
    return (
      <PageShell>
        <CheckCircle2 size={52} color="#22c55e" strokeWidth={1.5}
          style={{ filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.4))', marginBottom: 16 }} />
        <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', textAlign: 'center' }}>
          Already Verified
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 10, textAlign: 'center', lineHeight: 1.6 }}>
          This email address was already verified.
        </div>
        <a href="/login" style={{
          marginTop: 24, display: 'inline-block', padding: '12px 28px',
          background: 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 100%)',
          borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13,
          textDecoration: 'none', letterSpacing: '0.08em',
        }}>
          Sign In →
        </a>
      </PageShell>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  return (
    <PageShell>
      <XCircle size={52} color="#f87171" strokeWidth={1.5}
        style={{ filter: 'drop-shadow(0 0 20px rgba(248,113,113,0.4))', marginBottom: 16 }} />
      <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', textAlign: 'center' }}>
        Verification Failed
      </div>
      <div style={{
        fontSize: 13, color: '#94a3b8', marginTop: 10, textAlign: 'center',
        lineHeight: 1.6, padding: '0 8px',
      }}>
        {errorMsg}
      </div>

      {/* Resend form */}
      <div style={{
        marginTop: 28, width: '100%',
        padding: '18px 16px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.18)',
        borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: '#3b82f6',
          letterSpacing: '0.12em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Mail size={13} />
          Request a New Link
        </div>
        <form onSubmit={handleResend} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            placeholder="Your email address"
            value={resendEmail}
            onChange={e => setResendEmail(e.target.value)}
            disabled={resending}
            style={{
              width: '100%', boxSizing: 'border-box' as const,
              background: '#060f1f', border: '1.5px solid rgba(59,130,246,0.22)',
              borderRadius: 8, color: '#e2e8f0', fontSize: 14,
              padding: '11px 14px', outline: 'none', fontFamily: 'inherit',
              opacity: resending ? 0.6 : 1,
            }}
          />
          <button
            type="submit"
            disabled={resending || !resendEmail.trim()}
            style={{
              width: '100%', padding: '12px',
              background: resending || !resendEmail.trim()
                ? 'rgba(37,99,235,0.35)'
                : 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 100%)',
              border: 'none', borderRadius: 8, color: '#fff',
              fontSize: 13, fontWeight: 700, letterSpacing: '0.08em',
              cursor: resending || !resendEmail.trim() ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontFamily: 'inherit',
            }}
          >
            {resending && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {resending ? 'Sending…' : 'Resend Verification Email'}
          </button>
        </form>
        {resendStatus && (
          <div style={{
            fontSize: 12, color: resendStatus.startsWith('✅') ? '#22c55e' : '#f87171',
            textAlign: 'center', lineHeight: 1.5,
          }}>
            {resendStatus}
          </div>
        )}
      </div>

      <a href="/login" style={{
        marginTop: 20, fontSize: 12, color: '#3b82f6',
        textDecoration: 'none', textAlign: 'center',
      }}>
        ← Back to Sign In
      </a>
    </PageShell>
  );
}
